<script setup lang="ts">
import { ref } from 'vue'
import { makeImgErrorHandler } from '@shared/composables/useImgFallback'
import { PROPERTY_IMAGE_PLACEHOLDER } from '../../browse/constants/propertyPlaceholder'

import Box from '@uiKit/elements/Box.vue'
import AppImage from '@uiKit/elements/Image.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Text from '@uiKit/elements/Text.vue'
import Galleria from '@uiKit/elements/Galleria.vue'

const onImgError = makeImgErrorHandler(PROPERTY_IMAGE_PLACEHOLDER)

interface Props {
  mainImage: string
  thumbnails: [string, string]
  allImages: string[]
  isBookmarked?: boolean
  hideBookmark?: boolean
}

const isSingleImage = (images: string[]) => images.length <= 1

withDefaults(defineProps<Props>(), {
  isBookmarked: false,
  hideBookmark: false,
})

defineEmits<{ (e: 'bookmark'): void }>()

const galleryOpen = ref(false)
</script>

<template>
  <Box class="gallery" :class="{ 'gallery--single': isSingleImage(allImages) }">
    <!-- Main image -->
    <Box
      class="gallery__main"
      :class="{ 'gallery__main--full': isSingleImage(allImages) }"
      :role="isSingleImage(allImages) ? 'button' : undefined"
      :tabindex="isSingleImage(allImages) ? 0 : undefined"
      :aria-label="isSingleImage(allImages) ? 'Open property gallery' : undefined"
      @click="isSingleImage(allImages) ? (galleryOpen = true) : undefined"
    >
      <AppImage :src="mainImage" alt="Property" image-class="gallery__main-img" @error="onImgError" />

      <!-- Bookmark — top-right of the hero image, 44 px hit target. -->
      <Box
        v-if="!hideBookmark"
        is="button"
        type="button"
        align="center"
        justify="center"
        class="gallery__bookmark"
        :aria-label="isBookmarked ? 'Remove from saved properties' : 'Save property for later'"
        :aria-pressed="isBookmarked"
        @click.stop="$emit('bookmark')"
      >
        <PiIcon :name="isBookmarked ? 'bookmark-fill' : 'bookmark'" class="gallery__bookmark-icon" />
      </Box>

      <!-- View Gallery on main image when only one image -->
      <Box
        v-if="isSingleImage(allImages)"
        align="center"
        gap="1"
        class="gallery__view-btn gallery__view-btn--main"
      >
        <PiIcon name="images" class="gallery__view-icon" />
        <Text type="micro" is="span" color="var(--p-surface-0)" class="gallery__view-label">
          View gallery
        </Text>
      </Box>
    </Box>

    <!-- Thumbnails: only when multiple images -->
    <Box v-if="!isSingleImage(allImages)" direction="col" gap="3" class="gallery__thumbs">
      <Box class="gallery__thumb">
        <AppImage
          :src="thumbnails[0]"
          alt="Property thumbnail"
          image-class="gallery__thumb-img"
          @error="onImgError"
        />
      </Box>
      <Box
        class="gallery__thumb gallery__thumb--overlay"
        role="button"
        tabindex="0"
        aria-label="Open property gallery"
        @click="galleryOpen = true"
        @keydown.enter="galleryOpen = true"
        @keydown.space.prevent="galleryOpen = true"
      >
        <AppImage
          :src="thumbnails[1]"
          alt="Property thumbnail"
          image-class="gallery__thumb-img"
          @error="onImgError"
        />
        <Box align="center" gap="1" class="gallery__view-btn">
          <PiIcon name="images" class="gallery__view-icon" />
          <Text type="micro" is="span" color="var(--p-surface-0)" class="gallery__view-label">
            View gallery
          </Text>
        </Box>
      </Box>
    </Box>

    <!-- Full-screen gallery -->
    <Galleria v-model:visible="galleryOpen" :images="allImages" />
  </Box>
</template>

<style scoped>
.gallery {
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 340px;
  gap: 10px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
}

.gallery__main {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
}

.gallery__main--full {
  grid-column: 1 / -1;
  cursor: pointer;
}

.gallery :deep(.gallery__main-img) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Thumbnails column */
.gallery__thumb {
  flex: 1;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  min-height: 0;
}

.gallery :deep(.gallery__thumb-img) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.gallery__thumb--overlay {
  cursor: pointer;
}

.gallery__thumb--overlay :deep(.gallery__thumb-img) {
  filter: brightness(0.75);
}

.gallery__view-btn {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(29, 35, 50, 0.55);
  backdrop-filter: blur(8px);
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  z-index: 1;
  white-space: nowrap;
}

.gallery__view-btn--main {
  top: unset;
  left: unset;
  bottom: 14px;
  right: 14px;
  transform: none;
  padding: 5px 10px;
  border-radius: 7px;
}

.gallery__view-icon {
  font-size: 11px;
  color: var(--p-surface-0);
}

.gallery__view-label {
  letter-spacing: -0.02em;
}

.gallery__main:focus-visible,
.gallery__thumb--overlay:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

/* Bookmark in the gallery corner — 44 px hit target on a tinted-glass disc
   so it's visible on bright hero photos but doesn't overpower them. */
.gallery__bookmark {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(6px);
  cursor: pointer;
  color: var(--p-surface-0);
  z-index: 2;
  transition: background 0.15s ease, transform 0.15s ease;
}

.gallery__bookmark:hover {
  background: rgba(0, 0, 0, 0.6);
  transform: scale(1.05);
}

.gallery__bookmark:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.gallery__bookmark-icon {
  font-size: 18px;
}

@media (max-width: 600px) {
  .gallery {
    grid-template-columns: 1fr;
    grid-template-rows: 200px 100px;
  }

  .gallery--single {
    grid-template-rows: 200px;
  }

  .gallery__thumbs {
    flex-direction: row;
  }
}
</style>
